/// <amd-module name="Wit/Common/BacklogContent/IBacklogShortcutService" />
import { IIterationData } from "Agile/Contracts/Common";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssKeyboardShortcutService } from "VSS/Platform/KeyboardShortcuts";
import { TreeItemProvider } from "VSSUI/Utilities/TreeItemProvider";
import * as Resources from "Wit/Common/BacklogContent/Resources";
import { IBacklogTreeRow } from "Wit/Common/BacklogContentService/BacklogContentTreeTypes";
import { ICommonBacklogContentService } from "Wit/Common/BacklogContentService/ICommonBacklogContentService";
import { IHotKeyEvent, IHotKeyEventData, IHotKeyEventService, IHotKeyEventType } from "Wit/Common/HotKeyEventService/IHotKeyEventService";

export interface IBacklogShortcutService extends IVssService {
    registerShortcuts: (
        expandLevel: (itemProvider: TreeItemProvider<IBacklogTreeRow> | undefined) => void,
        collapseLevel: (itemProvider: TreeItemProvider<IBacklogTreeRow> | undefined) => void,
        updateIterations: (index: number, iteration?: IIterationData) => void,
        toggleFilter: () => void,
        selectAllItems: (pageContext: IVssPageContext) => void,
        showParents?: () => void,
        moveToTop?: () => void,
        toggleFullScreen?: () => void
    ) => void;
    unregisterShortcuts: () => void;
}

class BacklogShortcutService extends VssService implements IBacklogShortcutService {
    private shortcutService: IVssKeyboardShortcutService;
    private backlogContentService: ICommonBacklogContentService;

    public _serviceStart(pageContext: IVssPageContext) {
        this.pageContext = pageContext;
        this.shortcutService = this.pageContext.getService<IVssKeyboardShortcutService>("IVssKeyboardShortcutService");
        this.backlogContentService = this.pageContext.getService<ICommonBacklogContentService>("ICommonBacklogContentService");
    }

    public _serviceEnd() {}

    public registerShortcuts = (
        expandLevel: (itemProvider: TreeItemProvider<IBacklogTreeRow> | undefined) => void,
        collapseLevel: (itemProvider: TreeItemProvider<IBacklogTreeRow> | undefined) => void,
        updateIterations: (index: number) => void,
        toggleFilter: () => void,
        selectAllItems: (pageContext: IVssPageContext) => void,
        showParents?: () => void,
        moveToTop?: () => void,
        toggleFullScreen?: () => void
    ) => {
        // Shortcuts hidden from helper menu
        this.shortcutService.registerShortcut(Resources.BacklogsCapital, {
            combos: ["+"],
            action: () => {
                /// expand one level
                expandLevel(this.backlogContentService.getBacklogTree().value?.itemProvider);
            },
            description: Resources.ExpandOneLevel,
            hideFromHelpDialog: true
        });
        this.shortcutService.registerShortcut(Resources.BacklogsCapital, {
            combos: ["-"],
            action: () => {
                /// collapse one level
                collapseLevel(this.backlogContentService.getBacklogTree().value?.itemProvider);
            },
            description: Resources.CollapseOneLevel,
            hideFromHelpDialog: true
        });
        this.shortcutService.registerShortcut(Resources.BacklogsCapital, {
            combos: ["mod+a"],
            action: () => {
                /// select all the items
                selectAllItems(this.pageContext);
            },
            description: Resources.SelectAllItems,
            hideFromHelpDialog: true
        });

        // Shortcuts on helper menu
        if (moveToTop) {
            this.shortcutService.registerShortcut(Resources.BacklogsCapital, {
                combos: ["mod+home"],
                description: Resources.MoveItemToTop,
                action: () => {
                    // 0 = index in team settings that "Backlog" iteration is once we flatten the iterations
                    moveToTop();
                }
            });
        }
        this.shortcutService.registerShortcut(Resources.BacklogsCapital, {
            combos: ["m b"],
            description: Resources.MoveItemToBacklog,
            action: () => {
                // 0 = index in team settings that "Backlog" iteration is once we flatten the iterations
                updateIterations(0);
            }
        });
        this.shortcutService.registerShortcut(Resources.BacklogsCapital, {
            combos: ["m i"],
            description: Resources.MoveItemToCurrentIteration,
            action: () => {
                // 1 = index in team settings that "Current" iteration is once we flatten the iterations
                updateIterations(1);
            }
        });
        this.shortcutService.registerShortcut(Resources.BacklogsCapital, {
            combos: ["m n"],
            description: Resources.MoveItemToNextIteration,
            action: () => {
                // 2 = index in team settings that "Next" iteration is once we flatten the iterations
                updateIterations(2);
            }
        });

        this.shortcutService.registerShortcut(Resources.BacklogsCapital, {
            combos: ["ins", "f+i"],
            description: Resources.InsertChildItem,
            element: ".backlogs-view",
            action: () => {
                const elements = document.getElementsByClassName("add-line-button");
                if (elements && elements.length) {
                    const selection = this.backlogContentService.getTreeSelection();
                    if (selection.value && selection.value.length) {
                        const htmlElement = elements[selection.value[0].beginIndex] as HTMLElement;
                        htmlElement.click();
                    }
                }
            }
        });

        this.shortcutService.registerShortcut(Resources.BacklogsCapital, {
            combos: ["mod+shift+f"],
            description: Resources.ToggleFilter,
            action: () => {
                toggleFilter();

                this.triggerHotKeyEvent(IHotKeyEventType.FilterToggled);
            }
        });

        if (showParents) {
            this.shortcutService.registerShortcut(Resources.BacklogsCapital, {
                combos: ["r"],
                description: Resources.ShowHideParents,
                action: () => {
                    showParents();
                }
            });
        }

        if (toggleFullScreen) {
            this.shortcutService.registerShortcut(Resources.BacklogsCapital, {
                combos: ["z"],
                description: Resources.ToggleFullScreenMode,
                action: () => {
                    toggleFullScreen();
                }
            });
        }

        this.shortcutService.registerShortcut(Resources.BacklogsCapital, {
            combos: ["del"],
            description: Resources.Delete,
            action: () => {
                this.triggerHotKeyEvent(IHotKeyEventType.WorkItemDelete);
            }
        });

        this.shortcutService.registerShortcut(Resources.BacklogsCapital, {
            combos: ["mod+c"],
            description: Resources.CopyWorkItemsAsHtml,
            action: () => {
                this.triggerHotKeyEvent(IHotKeyEventType.WorkItemCopy);
            },
            allowPropagation: true
        });
    };

    public unregisterShortcuts = () => {
        this.shortcutService.removeShortcutGroup(Resources.BacklogsCapital);
    };

    private triggerHotKeyEvent = (eventType: IHotKeyEvent, eventData?: IHotKeyEventData) => {
        const hotKeyEventService = this.pageContext.getService<IHotKeyEventService>("IHotKeyEventService");
        hotKeyEventService.fireEvent(eventData ?? {}, eventType);
    };
}

Services.add("IBacklogShortcutService", { serviceFactory: BacklogShortcutService });
